import type { CSSProperties } from 'react';
import {
  CHIP_DENOMS,
  DECK_SUITS,
  type CasinoTheme,
  type ChipSetId,
  type DeckId,
  type FeltId,
} from '@tv-casino/shared';
import { themeTokens } from '@tv-casino/viewmodels/lib/themeTokens.js';

/**
 * A casino's theme as the custom properties the stylesheets already read
 * (defaults in `styles/global.css`). Set on a screen's root, it re-dresses
 * everything under it — the table, the layouts, the chip rails, the cards —
 * without any component knowing a theme exists.
 *
 * Every value comes off `themeTokens`, the renderer-neutral record the
 * native shells read too — this file only names the CSS custom properties.
 */

/** One felt as `--felt-deep`, `--felt`, `--felt-light` and `--felt-deep-rgb`. */
export function feltVars(id: FeltId): CSSProperties {
  const t = themeTokens({ felt: id });
  return {
    '--felt-deep': t.felt.deep,
    '--felt': t.felt.base,
    '--felt-light': t.felt.light,
    '--felt-deep-rgb': t.felt.deepRgb,
  } as CSSProperties;
}

/** One chip set as `--chip5-top` … `--chip500-edge`, a face per denomination. */
export function chipVars(id: ChipSetId): CSSProperties {
  const vars: Record<string, string> = {};
  for (const denom of CHIP_DENOMS) {
    const face = themeTokens({ chips: id }).chips.faces[denom];
    vars[`--chip${denom}-top`] = face.top;
    vars[`--chip${denom}-rim`] = face.rim;
    vars[`--chip${denom}-ink`] = face.ink;
    vars[`--chip${denom}-edge`] = face.edge;
  }
  return vars as CSSProperties;
}

/**
 * One deck as `--card-back-{ground,pattern,border}`, `--card-paper`,
 * `--card-edge` and `--card-ink-{S,H,D,C}`. A ground that names a felt
 * variable resolves on the element these land on, so set them beside the felt.
 */
export function deckVars(id: DeckId): CSSProperties {
  const t = themeTokens({ deck: id });
  const vars: Record<string, string> = {
    '--card-back-ground': t.deck.back.ground,
    '--card-back-pattern': t.deck.back.pattern,
    '--card-back-border': t.deck.back.border,
    '--card-paper': t.deck.face.paper,
    '--card-edge': t.deck.face.edge,
  };
  for (const suit of DECK_SUITS) vars[`--card-ink-${suit}`] = t.deck.face.suits[suit];
  return vars as CSSProperties;
}

/** The whole theme for a screen root. Missing or unknown parts read as the defaults. */
export function themeStyle(theme: CasinoTheme | null | undefined): CSSProperties {
  const t = themeTokens(theme);
  const vars: Record<string, string> = {
    '--felt-deep': t.felt.deep,
    '--felt': t.felt.base,
    '--felt-light': t.felt.light,
    '--felt-deep-rgb': t.felt.deepRgb,
    '--card-back-ground': t.deck.back.ground,
    '--card-back-pattern': t.deck.back.pattern,
    '--card-back-border': t.deck.back.border,
    '--card-paper': t.deck.face.paper,
    '--card-edge': t.deck.face.edge,
  };
  for (const denom of CHIP_DENOMS) {
    const face = t.chips.faces[denom];
    vars[`--chip${denom}-top`] = face.top;
    vars[`--chip${denom}-rim`] = face.rim;
    vars[`--chip${denom}-ink`] = face.ink;
    vars[`--chip${denom}-edge`] = face.edge;
  }
  for (const suit of DECK_SUITS) vars[`--card-ink-${suit}`] = t.deck.face.suits[suit];
  return vars as CSSProperties;
}
